'use client';

import Link from 'next/link';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAppStore } from '@/store/app';
import { translate } from '@/lib/i18n';
import type { DataHealthSummary, DataHealthStatus } from '@glintfans/shared';

const SRC_KEY: Record<string, string> = {
  'market.tickers': 'srcTickers',
  'market.spotTickers': 'srcSpotTickers',
  'market.externalTickers': 'srcExternalTickers',
  'market.overview': 'srcOverview',
  'market.derivatives': 'srcDerivatives',
  'news.feed': 'srcNews',
  'calendar.events': 'srcCalendar',
  'hl.onchain': 'srcHlOnchain',
  'liquidation.events': 'srcLiqEvents',
};

/**
 * N01 数据健康中心面板：
 * - 桌面：右侧 420px 滑出侧栏
 * - 手机：底部说明层（max-h 60vh，可滚动）
 * - 仅展示当前页面相关数据源状态，不让用户面对整个后端监控台
 * - 文案严格跟随用户语言（i18n），不硬编码
 */
export default function HealthPanel({ open, onClose, scope }: { open: boolean; onClose: () => void; scope?: string }) {
  const language = useAppStore((s) => s.language);
  const t = translate(language).health;
  const { data, isFetching } = useQuery({
    queryKey: ['health', scope ?? 'all'],
    queryFn: () => api.get<DataHealthSummary>(`/health-center${scope ? `?scope=${encodeURIComponent(scope)}` : ''}`),
    refetchInterval: 60_000,
    enabled: open,
  });

  if (!open) return null;

  const overall =
    !data || isFetching
      ? { label: t.checking, cls: 'bg-gf-muted' }
      : data.failedCount > 0
        ? { label: `${data.failedCount} ${t.sourcesFailed}`, cls: 'bg-gf-negative' }
        : data.staleCount > 0
          ? { label: `${data.staleCount} ${t.sourcesDelayed}`, cls: 'bg-gf-warn' }
          : { label: t.sourcesOk, cls: 'bg-gf-positive' };

  const statusMeta: Record<DataHealthStatus, { label: string; dot: string }> = {
    ok: { label: t.ok, dot: 'bg-gf-positive' },
    stale: { label: t.stale, dot: 'bg-gf-warn' },
    failed: { label: t.failed, dot: 'bg-gf-negative' },
    unknown: { label: t.unknown, dot: 'bg-gf-muted' },
  };

  const fmtTime = (ts?: number) => {
    if (!ts) return t.noCoverage;
    const d = new Date(ts);
    const p = (n: number) => String(n).padStart(2, '0');
    return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
  };
  const fmtAge = (ts?: number) => {
    if (!ts) return t.noCoverage;
    const s = Math.max(0, Math.floor((Date.now() - ts) / 1000));
    if (s < 60) return `${s} ${t.secAgo}`;
    if (s < 3600) return `${Math.floor(s / 60)} ${t.minAgo}`;
    return `${Math.floor(s / 3600)} ${t.hourAgo}`;
  };
  const fmtLatency = (ms?: number) => {
    if (ms == null) return t.noCoverage;
    return ms > 0 ? `${(ms / 1000).toFixed(1)} ${t.seconds}` : t.instant;
  };

  const sourceLabel = (s: { source: string; label: string }) =>
    t[SRC_KEY[s.source] ?? ''] || s.label;

  return (
    <>
      {/* 遮罩（点击关闭） */}
      <div className="fixed inset-0 top-16 z-40 bg-black/40" aria-hidden="true" onClick={onClose} />

      {/* 桌面：右侧 420px 侧栏 */}
      <div className="fixed right-0 top-16 z-[80] hidden h-[calc(100vh-4rem)] w-[420px] flex-col border-l border-gf-border bg-gf-bg shadow-xl md:flex">
        <PanelBody data={data} isFetching={isFetching} overall={overall} statusMeta={statusMeta} fmtTime={fmtTime} fmtAge={fmtAge} fmtLatency={fmtLatency} t={t} sourceLabel={sourceLabel} onClose={onClose} />
      </div>

      {/* 手机：底部说明层 */}
      <div className="fixed inset-x-0 top-[40vh] z-[80] flex h-[60vh] flex-col rounded-t-2xl border border-gf-border bg-gf-bg shadow-xl md:hidden">
        <div className="flex items-center justify-between border-b border-gf-border px-4 py-3">
          <div className="flex items-center gap-2">
            <span className={`h-2.5 w-2.5 rounded-full ${overall.cls}`} />
            <span className="text-sm font-semibold text-gf-text">{overall.label}</span>
          </div>
          <div className="flex items-center gap-3">
            <Link href="/health" className="text-xs text-gf-accent" onClick={onClose}>
              {t.viewDetails}
            </Link>
            <button type="button" onClick={onClose} className="rounded-md p-1 text-gf-muted hover:text-gf-text" aria-label={t.close}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-3">
          <SourceList data={data} isFetching={isFetching} statusMeta={statusMeta} fmtTime={fmtTime} fmtAge={fmtAge} fmtLatency={fmtLatency} t={t} sourceLabel={sourceLabel} />
        </div>
      </div>
    </>
  );
}

function PanelBody({ data, isFetching, overall, statusMeta, fmtTime, fmtAge, fmtLatency, t, sourceLabel, onClose }: {
  data?: DataHealthSummary;
  isFetching: boolean;
  overall: { label: string; cls: string };
  statusMeta: Record<DataHealthStatus, { label: string; dot: string }>;
  fmtTime: (ts?: number) => string;
  fmtAge: (ts?: number) => string;
  fmtLatency: (ms?: number) => string;
  t: Record<string, string>;
  sourceLabel: (s: { source: string; label: string }) => string;
  onClose: () => void;
}) {
  return (
    <>
      <div className="flex items-center justify-between border-b border-gf-border px-4 py-3">
        <div className="flex items-center gap-2">
          <span className={`h-2.5 w-2.5 rounded-full ${overall.cls}`} />
          <span className="text-sm font-semibold text-gf-text">{overall.label}</span>
        </div>
        <div className="flex items-center gap-3">
          <Link href="/health" className="text-xs text-gf-accent" onClick={onClose}>
            {t.viewDetails}
          </Link>
          <button type="button" onClick={onClose} className="rounded-md p-1 text-gf-muted hover:text-gf-text" aria-label={t.close}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-3">
        <SourceList data={data} isFetching={isFetching} statusMeta={statusMeta} fmtTime={fmtTime} fmtAge={fmtAge} fmtLatency={fmtLatency} t={t} sourceLabel={sourceLabel} />
      </div>
    </>
  );
}

function SourceList({ data, isFetching, statusMeta, fmtTime, fmtAge, fmtLatency, t, sourceLabel }: {
  data?: DataHealthSummary;
  isFetching: boolean;
  statusMeta: Record<DataHealthStatus, { label: string; dot: string }>;
  fmtTime: (ts?: number) => string;
  fmtAge: (ts?: number) => string;
  fmtLatency: (ms?: number) => string;
  t: Record<string, string>;
  sourceLabel: (s: { source: string; label: string }) => string;
}) {
  if (isFetching && !data) {
    return <div className="py-8 text-center text-sm text-gf-muted">{t.checkingStatus}</div>;
  }
  if (!data || data.total === 0) {
    return (
      <div className="py-8 text-center text-sm text-gf-muted">
        {t.noHealthRecords}
        <div className="mt-2 text-xs text-gf-muted/70">{t.healthNote}</div>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {data.sources.map((s) => {
        const m = statusMeta[s.status] ?? statusMeta.unknown;
        return (
          <div key={s.source} className="rounded-lg border border-gf-border bg-gf-surface p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2">
                <span className={`h-2 w-2 shrink-0 rounded-full ${m.dot}`} />
                <span className="truncate text-sm font-medium text-gf-text">{sourceLabel(s)}</span>
              </div>
              <span className="shrink-0 text-xs text-gf-muted">{m.label}</span>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-gf-muted">
              <div>{t.lastSuccessAge}：<span className="text-gf-text">{fmtAge(s.lastSuccess)}</span></div>
              <div>{t.lastSuccessAt}：<span className="text-gf-text">{fmtTime(s.lastSuccess)}</span></div>
              <div>{t.latency}：<span className="text-gf-text">{fmtLatency(s.latencyMs)}</span></div>
              <div>{t.coverage}：<span className="text-gf-text">{s.covered != null ? `${s.covered} ${t.items}` : t.noCoverage}</span></div>
            </div>
            {s.missing && <div className="mt-1.5 rounded bg-gf-negative/10 px-2 py-1 text-xs text-gf-negative">{t.missing}：{s.missing}</div>}
            {s.note && !s.missing && <div className="mt-1.5 text-xs text-gf-muted">{s.note}</div>}
            {s.errorCount > 0 && <div className="mt-1 text-xs text-gf-muted">{t.consecutiveFailures} {s.errorCount} {t.autoRetry}</div>}
          </div>
        );
      })}
    </div>
  );
}
